import { PlayerName } from "@/components/PlayerCardDialog";
import { ArchetypeBadge } from "@/components/ArchetypeBadge";
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Loader2, Crosshair } from "lucide-react";
import { PairingV2Panel } from "@/components/PairingV2Panel";
import { MasterPairingBoard } from "@/components/MasterPairingBoard";

const API = process.env.REACT_APP_BACKEND_URL;

const POS_COLOR = {
  C: "text-cyan-300 bg-cyan-950/60",
  LW: "text-emerald-300 bg-emerald-950/60",
  RW: "text-amber-300 bg-amber-950/60",
  D: "text-fuchsia-300 bg-fuchsia-950/60",
};

const PlayerLine = ({ p, team }) => (
  <div className="flex flex-wrap items-center gap-1.5 font-mono text-[10px] leading-tight text-slate-300" data-testid={`unit-player-${p.jersey}`}>
    <span className="font-bold text-cyan-400">#{p.jersey ?? "—"}</span>
    <PlayerName name={p.name} team={team} className="font-semibold uppercase text-slate-100" />
    <span className="rounded bg-slate-800 px-1 text-[9px] text-slate-400">{p.slot}</span>
    <ArchetypeBadge name={p.archetype} testId={`unit-arch-${p.jersey}`} />
    {p.pp_unit && <span className="rounded bg-amber-950/60 px-1 text-[9px] text-amber-300">{p.pp_unit}</span>}
    {p.origin_goals > 0 && (
      <span className="text-[9px] font-bold text-lime-400">{p.origin_goals}G/{p.origin_games}GM</span>
    )}
  </div>
);

const Unit = ({ u }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-2.5" data-testid={`unit-${u.team}-${u.position}`}>
    <div className="mb-1.5 flex flex-wrap items-center gap-1.5 font-mono text-xs">
      <span className="font-bold text-slate-100">{u.team}</span>
      <span className={`rounded px-1 font-bold ${POS_COLOR[u.position] || "bg-slate-800 text-slate-300"}`}>{u.position}</span>
      <span className="text-[10px] text-slate-500">vs {u.opponent}</span>
      <span className="text-[10px] font-bold text-rose-300">{u.opp_ga_pos} {u.source === "GOALIE ORIGIN" ? "G/GM" : "GA/GP"}</span>
      {u.source === "GOALIE ORIGIN" && <span className="rounded bg-lime-950/60 px-1 text-[8px] font-bold uppercase tracking-wider text-lime-400">goalie origin</span>}
    </div>
    <div className="space-y-1">
      {(u.players || []).length > 0 ? (
        u.players.map((p, i) => <PlayerLine key={`${p.jersey}-${i}`} p={p} team={u.team} />)
      ) : (
        <span className="font-mono text-[10px] text-slate-600">lineup unavailable</span>
      )}
    </div>
  </div>
);

const Rate = ({ label, r }) => r ? (
  <span className="text-slate-400">{label} <b className="text-slate-200">{r.hit_pct}%</b> <span className="text-slate-600">({r.scorers}/{r.players} · {r.goals}G)</span></span>
) : <span className="text-slate-600">{label} —</span>;

const ArchUnit = ({ u, team }) => (
  <div className="space-y-1">
    {u.players.length ? u.players.map((pl, i) => (
      <div key={`${pl.jersey}-${i}`} className="flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid={`arch-audit-${team}-${pl.jersey}`}>
        <span className="font-semibold uppercase text-slate-200">{pl.name}</span>
        <span className="text-fuchsia-300">{pl.archetype || "—"}</span>
        {pl.rate ? (<>
          <Rate label="SZN" r={pl.rate.season} /><Rate label="L7" r={pl.rate.last7} />
          {pl.rate.tonight_used && <Rate label="TON" r={pl.rate.tonight} />}
          <span className="text-cyan-300">adj {pl.rate.rate}% · {pl.rate.lift}x · rel {pl.rate.reliability}</span>
        </>) : <span className="text-slate-600">no graded history</span>}
      </div>
    )) : <span className="text-slate-600">lineup unavailable</span>}
  </div>
);

const RankDetail = ({ p }) => {
  const a = p.archetype_audit;
  if (!a) return null;
  return (
    <details className="mt-2 rounded-lg border border-slate-800 bg-slate-950/40 px-2.5 py-1.5 font-mono text-[10px]" data-testid={`pos-pair-detail-${p.pair_id}`}>
      <summary className="cursor-pointer select-none uppercase tracking-wider text-slate-500 hover:text-slate-300">
        Rank {p.rank_score} · positional {a.positional_contribution} + archetype {a.archetype_contribution}
        {a.fallback && <span className="ml-1 text-amber-400">(positional only)</span>}
      </summary>
      <div className="mt-2 space-y-2 text-slate-400">
        <div><span className="text-slate-500">{p.unit_a.team} {p.unit_a.position}</span> <ArchUnit u={a.unit_a} team={p.unit_a.team} /></div>
        <div><span className="text-slate-500">{p.unit_b.team} {p.unit_b.position}</span> <ArchUnit u={a.unit_b} team={p.unit_b.team} /></div>
        <p className="text-slate-300" data-testid={`pos-pair-rationale-${p.pair_id}`}>{a.rationale}</p>
      </div>
    </details>
  );
};

export default function PositionPairingTab({ apiDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [engine, setEngine] = useState("baseline");

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/api/position-pairing`, { params: apiDate ? { date: apiDate } : {}, withCredentials: true });
      setData(r.data);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to build cheat sheet"); }
    finally { setLoading(false); }
  }, [apiDate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-24 text-slate-400" data-testid="pos-pairing-loading">
      <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
      <p className="mt-4 font-mono text-xs uppercase tracking-widest">Building cheat sheet…</p>
    </div>
  );
  if (err) return <div className="py-20 text-center font-mono text-sm text-rose-400" data-testid="pos-pairing-error">{err}</div>;
  if (!data) return null;

  return (
    <div className="space-y-5" data-testid="pos-pairing-dashboard">
      <MasterPairingBoard apiDate={apiDate} />
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-slate-800 pb-4">
        <div>
          <h2 className="flex items-center gap-2 font-mono text-lg font-black uppercase tracking-wider text-cyan-300">
            <Crosshair className="h-5 w-5" /> Position Pairing Cheat Sheet
          </h2>
          <p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-slate-500">
            Cross-game positional weakness matchups · {data.slate_size} games · players + archetypes
          </p>
          <p className="mt-1 font-mono text-[10px] text-amber-300/80" data-testid="pos-pairing-analytical-note">Analytical view · feeds the master pool's positional signal; not an official board</p>
          {engine === "baseline" && <p className="mt-1 font-mono text-[10px] text-slate-500" data-testid="pos-pairing-arch-meta">
            {data.archetype_engine?.available
              ? `Archetype hit rates ${Math.round(data.archetype_engine.weight * 100)}% of rank · league ${data.archetype_engine.baseline_hit_pct}% · season ${data.archetype_engine.season_graded} graded (${data.archetype_engine.season_nights} nights) · L7 ${data.archetype_engine.last7_graded}`
              : "No graded archetype history before this date. Ranked on positional weakness only."}
          </p>}
        </div>
        <div className="flex items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-slate-800 font-mono text-[10px] font-bold uppercase">
            {[["baseline", "Baseline (default)"], ["v2", "Engine v2 preview"]].map(([k, l]) => (
              <button key={k} data-testid={`pairing-engine-${k}`} onClick={() => setEngine(k)}
                className={`px-3 py-1.5 transition-colors ${engine === k ? "bg-cyan-600/25 text-cyan-200" : "bg-slate-900 text-slate-500 hover:text-slate-300"}`}>{l}</button>
            ))}
          </div>
          <div className="rounded-lg border border-cyan-500/30 bg-cyan-950/30 px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-cyan-300" data-testid="pairing-count">
            {engine === "baseline" ? `${data.pairs.length} pairings` : "Top 10 player pairs"}
          </div>
        </div>
      </div>

      {engine === "v2" ? <PairingV2Panel apiDate={apiDate} /> : (
      <div className="space-y-2" data-testid="pos-pairing-list">
        {data.pairs.map((p, i) => (
          <div key={p.pair_id} className={`rounded-xl border border-slate-800 bg-slate-900/30 p-3 ${i === 0 ? "ring-1 ring-cyan-500/30" : ""}`} data-testid={`pos-pair-${p.pair_id}`}>
            <div className="mb-2 flex items-center justify-between">
              <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-slate-500">
                #{i + 1} · {p.unit_a.team} {p.unit_a.position} + {p.unit_b.team} {p.unit_b.position}
              </span>
              <span className="rounded-lg border border-cyan-500/30 bg-cyan-950/30 px-2 py-1 font-mono text-[11px] font-bold text-cyan-300">
                Combined {p.combined_gapg} GA/GP
              </span>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
              <div className="flex-1"><Unit u={p.unit_a} /></div>
              <div className="flex items-center justify-center px-1 font-display text-xl font-bold text-slate-600">+</div>
              <div className="flex-1"><Unit u={p.unit_b} /></div>
            </div>
            <RankDetail p={p} />
          </div>
        ))}
      </div>
      )}
    </div>
  );
}
